import React, { useEffect, useRef } from 'react';
import { Card, Badge } from './Shared';
import SafeIcon from '../common/SafeIcon';
import * as echarts from 'echarts';

const StatCard = ({ title, value, trend, icon, color }) => (
  <Card>
    <div className="flex justify-between items-start">
      <div>
        <p className="text-slate-400 text-sm font-medium">{title}</p>
        <h3 className="text-2xl font-bold text-slate-100 mt-1">{value}</h3>
      </div>
      <div className={`p-3 rounded-lg bg-navy-900 border border-navy-700 text-${color}-400`}>
        <SafeIcon name={icon} className="text-xl" />
      </div>
    </div>
    <div className="mt-4 flex items-center gap-2">
      <Badge type={trend > 0 ? "success" : trend < 0 ? "danger" : "default"}>
        <SafeIcon name={trend > 0 ? "TrendingUp" : "TrendingDown"} className="mr-1 inline" />
        {Math.abs(trend)}%
      </Badge>
      <span className="text-xs text-slate-500">vs last month</span>
    </div>
  </Card>
);

const CostChart = () => {
  const chartRef = useRef(null);

  useEffect(() => {
    let instance = echarts.getInstanceByDom(chartRef.current);
    if (!instance) {
      instance = echarts.init(chartRef.current);
    }

    const option = {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'axis',
        backgroundColor: '#112240',
        borderColor: '#233554',
        textStyle: { color: '#ccd6f6' }
      },
      grid: { left: '3%', right: '4%', bottom: '3%', top: '10%', containLabel: true },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul'],
        axisLine: { lineStyle: { color: '#233554' } },
        axisLabel: { color: '#8892b0' }
      },
      yAxis: {
        type: 'value',
        splitLine: { lineStyle: { color: '#112240', type: 'dashed' } },
        axisLabel: { color: '#8892b0', formatter: '${value}k' }
      },
      series: [
        {
          name: 'Freight Costs',
          type: 'line',
          smooth: true,
          data: [120, 132, 101, 134, 90, 230, 210],
          itemStyle: { color: '#FF5A00' },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(255, 90, 0, 0.3)' },
              { offset: 1, color: 'rgba(255, 90, 0, 0)' }
            ])
          }
        },
        {
          name: 'Projected',
          type: 'line',
          smooth: true,
          data: [110, 120, 115, 140, 100, 210, 240],
          itemStyle: { color: '#64FFDA' },
          lineStyle: { type: 'dashed' }
        }
      ]
    };

    instance.setOption(option);

    const handleResize = () => instance.resize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      instance.dispose();
    };
  }, []);

  return <div ref={chartRef} style={{ width: '100%', height: '300px' }} />;
};

const Dashboard = () => {
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Control Tower Overview</h1>
          <p className="text-slate-400 mt-1">Real-time logistics matrix.</p>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
          </span>
          <span className="text-green-400 font-mono">SYSTEM ONLINE</span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Active Shipments" value="1,284" trend={12.5} icon="Package" color="cyan" />
        <StatCard title="Total Volume (TEU)" value="8,492" trend={5.2} icon="Box" color="orange" />
        <StatCard title="Exception Rate" value="3.2%" trend={-1.4} icon="AlertTriangle" color="green" />
        <StatCard title="Est. Cost Saving" value="$142.5k" trend={8.1} icon="DollarSign" color="cyan" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 flex flex-col">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold text-slate-200">Global Cost Trajectory</h3>
            <select className="bg-navy-900 border border-navy-600 text-slate-300 text-sm rounded-md px-2 py-1 outline-none">
              <option>Last 6 Months</option>
              <option>YTD</option>
            </select>
          </div>
          <div className="flex-grow">
            <CostChart />
          </div>
        </Card>

        <Card className="flex flex-col">
          <h3 className="font-semibold text-slate-200 mb-4">Live Network Status</h3>
          <div className="flex-grow flex flex-col gap-4 relative">
            {/* Abstract visual representation of a map/network */}
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-cyan-900/20 via-navy-900/0 to-navy-900/0 pointer-events-none" />
            
            <div className="space-y-3 z-10">
              {['Port of LA - Congested', 'Suez Canal - Clear', 'Frankfurt Air - Minor Delays'].map((node, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-navy-900/80 border border-navy-700 rounded-lg">
                  <div className="flex items-center gap-3">
                    <SafeIcon name="MapPin" className={i === 0 ? "text-alert-500" : i === 1 ? "text-green-500" : "text-orange-500"} />
                    <span className="text-sm font-medium text-slate-300">{node}</span>
                  </div>
                  <SafeIcon name="Activity" className="text-navy-500" />
                </div>
              ))}
            </div>
            
            <div className="mt-auto pt-4 border-t border-navy-700">
               <div className="flex justify-between text-sm mb-2">
                 <span className="text-slate-400">Network Efficiency</span>
                 <span className="text-cyan-400 font-mono">92%</span>
               </div>
               <div className="w-full bg-navy-900 rounded-full h-1.5">
                  <div className="bg-cyan-500 h-1.5 rounded-full w-[92%] shadow-[0_0_10px_rgba(100,255,218,0.5)]"></div>
               </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;